/**
 * video-thumb-fallback.js — extract a row of thumbnails from a video file.
 *
 * The naive HTML5 approach (`tempVideo.currentTime = t; await onseeked`) fails
 * silently on iPhone HEVC mp4 ("IMG_*.mov" / "IMG_*_cut.mp4"): the file's moov
 * atom parses cleanly so duration is known, but the OS decoder doesn't paint a
 * frame, every seek times out, and the loop ends with zero thumbs and a black
 * <video> element. User report 2026-04-27 (139.216.26.28, iOS 26.4.2 + Chrome
 * on iOS) — we patched video-trim with this fallback, then noticed the same
 * pattern in 9 other tools.
 *
 * Public API:
 *
 *   await window.__videoThumbFallback({
 *       file:        File,                            // source mp4/mov/...
 *       videoEl:     HTMLVideoElement | null,         // sets .poster from
 *                                                    // first FFmpeg frame
 *       duration:    number,                          // seconds — REQUIRED
 *       width:       number,                          // source video width  (px, for aspect)
 *       height:      number,                          // source video height (px, for aspect)
 *       count:       number,                          // thumbs to produce (8-30)
 *       isMobile:    boolean,                         // tighter timeouts/budgets
 *       slug:        string,                          // tool slug for telemetry
 *       frameWidth:  number,                          // optional, default 120
 *   });
 *   // → { frames: Array<{time, imageData}>, source: 'html5'|'ffmpeg'|'mixed'|'none', posterURL: string|null }
 *
 *   - frames is the same {time, imageData} shape every consumer already uses,
 *     so existing drawThumbnails() loops keep working unchanged.
 *   - source tells the caller which engine produced the result. For telemetry.
 *   - posterURL is a blob: URL that's been set on videoEl.poster (caller
 *     must revokeObjectURL when the workspace tears down — see returned URL).
 *
 * Strategy:
 *   1. Try HTML5 first — fastest path, no WASM warm-up. If decoder is healthy
 *      this completes in well under a second.
 *   2. If the loop produced ZERO frames (all seeks timed out), call FFmpeg
 *      WASM `process(file, ['-ss', t, '-frames:v', '1', '-vf', 'scale=...',
 *      '-f', 'image2'], 'frame.png')` for each thumb. FFmpeg has its own
 *      decoder, so it succeeds on iOS-broken HEVC. Slower but at least
 *      produces a result.
 *   3. The first FFmpeg-decoded frame is also stamped as videoEl.poster so
 *      the player area shows a real first frame instead of black.
 *
 * Telemetry events emitted (via window.__ae if present):
 *   - feature_use action='thumb_html5_zero', mob, slug
 *   - feature_use action='thumb_ffmpeg_ok' | 'thumb_ffmpeg_zero', picked, asked, slug
 */
(function(){
'use strict';
if (window.__videoThumbFallback && !window.__videoThumbFallback.__isStub) return;

async function tryHtml5(opts) {
    var thumbVideo = document.createElement('video');
    thumbVideo.muted = true;
    thumbVideo.preload = 'auto';
    thumbVideo.src = (opts.videoEl && opts.videoEl.src) || URL.createObjectURL(opts.file); try { thumbVideo.load(); } catch (_) { }
    var ownsBlobUrl = !(opts.videoEl && opts.videoEl.src);

    try {
        await new Promise(function(resolve, reject){
            thumbVideo.onloadeddata = resolve;
            thumbVideo.onerror = reject;
            setTimeout(reject, 15000);
        }).catch(function(){});

        if (!thumbVideo.duration) return [];

        var frameW = opts.frameWidth || 120;
        var frameH = Math.round(frameW * (opts.height / opts.width)) || 68;
        var interval = opts.duration / opts.count;
        var canvas = document.createElement('canvas');
        canvas.width = frameW; canvas.height = frameH;
        var ctx = canvas.getContext('2d', { willReadFrequently: true });

        var frames = [];
        var deadline = Date.now() + (opts.isMobile ? 20000 : 40000);
        var consecutiveFails = 0;

        for (var i = 0; i < opts.count; i++) {
            if (Date.now() > deadline) break;
            if (consecutiveFails >= 3) break;
            var seekTime = i * interval + interval / 2;
            try {
                // Wait for the seeked frame to be PRESENTED (bare seeked
                // races the compositor -> black thumbs, the exact failure
                // this fallback module exists to avoid).
                var seeked;
                if (window.__seekPresented) {
                    var via = await window.__seekPresented(thumbVideo, Math.min(seekTime, opts.duration - 0.1), opts.isMobile ? 1500 : 2500);
                    seeked = via !== 'timeout' && via !== 'seek_error';
                } else {
                    thumbVideo.currentTime = Math.min(seekTime, opts.duration - 0.1);
                    seeked = await new Promise(function(resolve){
                        thumbVideo.onseeked = function(){ resolve(true); };
                        setTimeout(function(){ resolve(false); }, opts.isMobile ? 1500 : 2500);
                    });
                }
                if (!seeked) { consecutiveFails++; continue; }
                consecutiveFails = 0;
                ctx.drawImage(thumbVideo, 0, 0, frameW, frameH);
                frames.push({ time: seekTime, imageData: ctx.getImageData(0, 0, frameW, frameH) });
            } catch (_) { consecutiveFails++; }
        }
        return frames;
    } finally {
        if (ownsBlobUrl) try { URL.revokeObjectURL(thumbVideo.src); } catch(_){}
        thumbVideo.src = '';
    }
}

async function tryFFmpeg(opts) {
    if (typeof window.FFmpegProcessor === 'undefined') return { frames: [], firstBlob: null };
    var fp = window.FFmpegProcessor;
    try {
        if (!fp.isReady()) await fp.init(function(){});
    } catch (_) { return { frames: [], firstBlob: null }; }

    var frameW = opts.frameWidth || 120;
    var frameH = Math.round(frameW * (opts.height / opts.width)) || 68;
    var canvas = document.createElement('canvas');
    canvas.width = frameW; canvas.height = frameH;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });

    var frames = [];
    var firstBlob = null;
    var lastErr = null;
    for (var k = 0; k < opts.count; k++) {
        try {
            var t = Math.max(0, Math.min((k + 0.5) * (opts.duration / opts.count), opts.duration - 0.05));
            // Single-image PNG: drop `-f image2` (which expects a %d pattern in
            // filename and rejects single-shot writes on FFmpeg WASM 5.1.4 for
            // some container/codec combos — matroska,webm seen in the wild).
            // PNG muxer is auto-detected from the .png extension.
            var pre = ['-ss', t.toFixed(3)];
            var args = ['-frames:v', '1', '-vf', 'scale=' + frameW + ':' + frameH];
            /* 2026-09-03 — потоковый монтаж вместо полной записи файла на КАЖДЫЙ
               кадр. process() делает writeFile всего исходника в MEMFS и удаляет
               его в finally, то есть N кадров = N полных копий: замер на 42.8 МБ
               файле — 8 кадров дали 327 МБ записей. processStreaming() монтирует
               (WORKERFS, без копирования) и принимает ТУ ЖЕ сигнатуру.
               Замерено дважды на форсированном ffmpeg-фолбэке (сики убиты, как у
               iPhone-HEVC — ровно та когорта, что сюда попадает):
                 process 55.4 с / 37.2 с и 327 МБ  ·  processStreaming 35.6 с / 31.1 с и 0 МБ.
               Третий кандидат — один проход `fps+tile` одной лентой — оказался
               МЕДЛЕННЕЕ (40.3 с / 32.3 с): он декодирует файл целиком, тогда как
               сики читают лишь по GOP, и на длинных файлах разрыв только растёт.
               Важнее секунд — снятые 327 МБ churn: сюда приходят слабые телефоны. */
            var _fpRun = (typeof fp.processStreaming === 'function') ? fp.processStreaming : fp.process;
            var r = await _fpRun.call(fp, opts.file, args, 'thumb_' + k + '.png', undefined, pre);
            if (!r || !r.blob || !r.blob.size) continue;
            // A thumbnail is an intermediate preview, never the tool's final
            // output — keep it out of the global blob completion auto-tracker
            // (app.blade) so it can't fire a phantom tool_complete.
            try { window.__skipBlobAutoTrack && window.__skipBlobAutoTrack(r.blob); } catch (_) {}
            var bmp = await createImageBitmap(r.blob).catch(function(){ return null; });
            if (!bmp) continue;
            ctx.clearRect(0, 0, frameW, frameH);
            ctx.drawImage(bmp, 0, 0, frameW, frameH);
            if (bmp.close) bmp.close();
            frames.push({ time: t, imageData: ctx.getImageData(0, 0, frameW, frameH) });
            if (k === 0) firstBlob = r.blob;
        } catch (e) {
            lastErr = e;
            // Per-frame failure: keep going. Telemetry on the first failure so
            // we can debug why FFmpeg can't produce a thumb when HTML5 also failed.
            if (k === 0) {
                try {
                    window.__ae && window.__ae('feature_use', {
                        slug: opts.slug || '',
                        action: 'thumb_ffmpeg_err',
                        msg: ((e && e.message) || String(e || '')).substring(0, 160),
                        ext: (opts.file && opts.file.name || '').split('.').pop().toLowerCase()
                    });
                } catch (_) {}
            }
        }
    }
    return { frames: frames, firstBlob: firstBlob };
}

// Per-file memo. Thumbnails are a pure function of (file, count, frameWidth) —
// but callers re-ask constantly: video-merge's renderClipList() rebuilds every
// clip element (and with it every thumbstrip request) on add / reorder / remove
// / transition pick. Measured 2026-07-29 on a 3-clip session with 3 ordinary
// clicks: 93 whole-file FFmpeg decodes. When HTML5 seeking yields nothing (the
// iPhone-HEVC class this module exists for) each of those is a real WASM decode,
// and ffmpeg.wasm never returns heap — so the run ends in "RuntimeError: memory
// access out of bounds" on a file that is nowhere near too big. Prod 30 d:
// video-merge 331 events / 118 sessions, out_name thumb_0..4.png, mostly <50 MB
// inputs on 8-32 GB devices (see reference_ffmpeg_wasm_heap_never_returned —
// device RAM is not the axis, exec count is).
//
// WeakMap keyed by the File object: entries die with the file, no manual purge.
// Negative results are memoised too but only after MAX_ATTEMPTS tries, so a
// genuinely transient failure (WASM core still loading on the first call) can
// still recover while a permanently-undecodable file can't restart the storm.
var _memo = new WeakMap();
var MAX_ATTEMPTS = 2;

function _memoKey(opts){
    return [opts.count, opts.frameWidth || 120, opts.noFfmpeg ? 1 : 0, Math.round(opts.duration || 0)].join(':');
}

async function _compute(opts) {
    var html5Frames = [];
    try { html5Frames = await tryHtml5(opts); } catch (_) {}

    if (html5Frames.length > 0) {
        return { frames: html5Frames, source: 'html5', firstBlob: null };
    }

    try { window.__ae && window.__ae('feature_use', { slug: opts.slug || '', action: 'thumb_html5_zero', mob: opts.isMobile ? 1 : 0 }); } catch (_) {}

    // opts.noFfmpeg: caller (e.g. video-merge on mobile / large files) opts out of
    // the heavy FFmpeg decode. Decoding a 100-400 MB HEVC just for a 160px preview
    // frame is slow and OOM/FILE_LOST-prone on iOS — the tool stays fully usable
    // with a placeholder, so we skip rather than block.
    if (opts.noFfmpeg) {
        try { window.__ae && window.__ae('feature_use', { slug: opts.slug || '', action: 'thumb_ffmpeg_skipped', mob: opts.isMobile ? 1 : 0 }); } catch (_) {}
        return { frames: [], source: 'none', firstBlob: null };
    }

    var ff = await tryFFmpeg(opts);
    try { window.__ae && window.__ae('feature_use', { slug: opts.slug || '', action: ff.frames.length > 0 ? 'thumb_ffmpeg_ok' : 'thumb_ffmpeg_zero', picked: ff.frames.length, asked: opts.count }); } catch (_) {}

    return { frames: ff.frames, source: ff.frames.length > 0 ? 'ffmpeg' : 'none', firstBlob: ff.firstBlob || null };
}

window.__videoThumbFallback = async function(opts) {
    opts = opts || {};
    if (!opts.file || !opts.duration || !opts.width || !opts.height || !opts.count) {
        return { frames: [], source: 'none', posterURL: null };
    }

    var perFile = _memo.get(opts.file);
    if (!perFile) { perFile = {}; _memo.set(opts.file, perFile); }
    var key = _memoKey(opts);
    var entry = perFile[key];

    if (!entry) entry = perFile[key] = { attempts: 0, result: null, inflight: null };

    var result;
    if (entry.result && (entry.result.frames.length > 0 || entry.attempts >= MAX_ATTEMPTS)) {
        result = entry.result;                       // decided — never decode again
    } else if (entry.inflight) {
        result = await entry.inflight;               // a concurrent render asked first
    } else {
        entry.attempts++;
        entry.inflight = _compute(opts).catch(function(){ return { frames: [], source: 'none', firstBlob: null }; });
        try {
            result = await entry.inflight;
            entry.result = result;
        } finally {
            entry.inflight = null;
        }
    }

    // Poster URL is minted per call, never cached: a caller that revokes its
    // blob: URL on teardown must not hand a dead URL to the next one.
    var posterURL = null;
    if (opts.videoEl && result.firstBlob) {
        try {
            posterURL = URL.createObjectURL(result.firstBlob);
            opts.videoEl.setAttribute('poster', posterURL);
        } catch (_) {}
    }

    return { frames: result.frames, source: result.source, posterURL: posterURL };
};

})();

// Drain stub queue installed by timbrica-core.js __sharedFunction
// (defer-onerror race defense — see helper comment for context).
if (typeof window.__drainSharedFunction === 'function') window.__drainSharedFunction('__videoThumbFallback');
